import { Space, Tooltip } from 'antd';
import moment, { ISO_8601 } from 'moment';
import React, { FC, useMemo } from 'react';
import styled from 'styled-components';
import { User } from 'api/models';
import { ApprovalObjectType, ApprovalPreview } from 'api/notifications';
import { ReactComponent as Approved } from 'assets/svgs/approved.svg';
import { DueDate } from 'components/atoms/due-date';
import { SquadAvatarList } from 'components/atoms/squad-avatar-list';
import { useGetAllActiveUsersMap } from 'hooks/use-idb';
import { useTranslation } from 'hooks/use-translation';
import { Header } from './components';
import styles from './group.module.css';

interface Props {
  approval: ApprovalPreview;
}

const ApprovedIcon = styled(Approved)`
  margin-right: var(--spacing-04);
`;

const getTitle = (approval: ApprovalPreview) => {
  switch (approval.approvalObjectType) {
    case ApprovalObjectType.Task:
      return approval.task.name;
    case ApprovalObjectType.Document:
      return approval.document.name;
    default:
      return undefined;
  }
};

const getSubtitle = (approval: ApprovalPreview) => {
  switch (approval.approvalObjectType) {
    case ApprovalObjectType.Task: {
      const {
        task: {
          project: { name: projectName },
          projectPhase,
        },
      } = approval;

      return `${projectName} ${
        projectPhase?.name != null ? `/ ${projectPhase.name}` : ''
      }`;
    }
    case ApprovalObjectType.Document: {
      const {
        document: {
          project: { name: projectName },
          space: { name: spaceName },
        },
      } = approval;

      return `${projectName} ${spaceName != null ? `/ ${spaceName}` : ''}`;
    }
    default:
      return undefined;
  }
};

export const ApprovalHeader: FC<Props> = ({ approval, children }) => {
  const { approverIds, dueDate, actedUpon } = approval;
  const users = useGetAllActiveUsersMap();
  const dueDateTitle = useTranslation('due_date');
  const approverTitle = useTranslation('approvals.approvers');

  const approvers = useMemo(
    () => approverIds.map((x) => users[x]) as User[],
    [approverIds, users],
  );

  const title = getTitle(approval) ?? '';
  const subtitle = getSubtitle(approval) ?? '';

  return (
    <Header className={styles.header}>
      <Header.Primary>
        <ApprovedIcon />
        <Header.PrimaryBlock>
          <div title={title} className={`${styles.title} ${styles.ellipsis}`}>
            {title}
          </div>
          <div
            title={subtitle}
            className={`${styles.subtitle} ${styles.ellipsis}`}
          >
            {subtitle}
          </div>
        </Header.PrimaryBlock>
      </Header.Primary>
      <Header.Secondary>
        <Space size={16}>
          {approvers.length > 0 ? (
            <Tooltip title={approverTitle}>
              <SquadAvatarList squad={{ users: approvers, teams: [] }} />
            </Tooltip>
          ) : null}
          {dueDate != null ? (
            <Tooltip
              title={`${dueDateTitle}: ${moment(dueDate, ISO_8601).format(
                'LL',
              )}`}
            >
              <span>
                <DueDate
                  className={styles.due}
                  timestamp={moment(dueDate, ISO_8601)}
                  completed={actedUpon}
                />
              </span>
            </Tooltip>
          ) : null}
        </Space>
      </Header.Secondary>
      <Header.Hanger>{children}</Header.Hanger>
    </Header>
  );
};
